
import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { MappedLeadData } from '../../types';
import { Card, CardHeader, SectionLabel, SimpleTabContent } from '../common/Atomic';
import { ResearchSummarySections } from '../common/ResearchSummarySections';
import { StreamdownRendererSimple } from '../common/StreamdownRenderer';
import { getResearchSummarySectionsForTab } from '../../utils/researchSummary';
import { cleanURL } from '../../utils/helpers';
import { buildCompanyTextGroups, isUsableText, mergeCompanySummaries } from './companyInsights';

interface CompanyTabProps {
  data: MappedLeadData;
}

interface CompanyDetailFact {
    label: string;
    value: string;
    href?: string;
}

interface CompanyContextFact {
    label: string;
    value: string;
}

const normalizeExternalURL = (value: string | null | undefined) => {
    if (!isUsableText(value) || value === '#') return null;
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const formatLinkedInURL = (linkedinId: string | null) => {
    if (!isUsableText(linkedinId) || linkedinId === '#') return null;
    if (/^https?:\/\//i.test(linkedinId)) return linkedinId;
    if (/^(?:www\.)?linkedin\.com/i.test(linkedinId)) return `https://${linkedinId}`;

    const cleanId = linkedinId.replace(/^\/+/, '');
    return `https://www.linkedin.com/company/${cleanId}`;
};

export const CompanyTab: React.FC<CompanyTabProps> = ({ data }) => {
    const [companyLogoError, setCompanyLogoError] = useState(false);
    const headingIdPrefix = React.useId();
    const companySummaryHeadingId = `${headingIdPrefix}-company-summary`;
    const companyDetailsHeadingId = `${headingIdPrefix}-company-details`;
    const researchSections = getResearchSummarySectionsForTab(data, 'company');
    const companyContextSection = researchSections.find((section) => section.normalizedTitle.includes('company context'));
    const remainingCompanyResearchSections = researchSections.filter((section) => section !== companyContextSection);
    const companyLevel1Id = data.companyLvl1?.id ?? data.rawOriginalData?.company_lvl1_id ?? null;
    const executiveSummary = isUsableText(data.companyGeneratorSummary)
        ? data.companyGeneratorSummary.trim()
        : isUsableText(data.rawCompanySummary?.executive_overview)
            ? data.rawCompanySummary.executive_overview.trim()
            : '';
    const websiteURL = normalizeExternalURL(data.companyWebsite);
    const linkedInURL = formatLinkedInURL(data.companyLinkedin);
    const companyLocation = [data.companyCity, data.companyState, data.companyCountry]
        .filter(isUsableText)
        .join(', ');
    const cnaeValue = isUsableText(data.cnae_text)
        ? String(data.cnae_text).split(',')[0].trim()
        : isUsableText(data.cnae_code)
            ? data.cnae_code.trim()
            : '';
    const employeeValue = isUsableText(data.companyEmployees)
        ? data.companyEmployees.trim()
        : isUsableText(data.size_range)
            ? data.size_range.trim()
            : '';
    const companyStage = isUsableText(data.companyStage) ? data.companyStage.trim() : '';
    const companySizeRange = isUsableText(data.size_range)
        ? data.size_range.trim()
        : isUsableText(data.companySize)
            ? data.companySize.trim()
            : employeeValue;
    const companyIndustry = isUsableText(data.companyIndustry) ? data.companyIndustry.trim() : '';
    const companyContextFacts: CompanyContextFact[] = [
        companyStage ? { label: 'Stage', value: companyStage } : null,
        companySizeRange ? { label: 'Size Range', value: companySizeRange } : null,
        companyIndustry ? { label: 'Industry', value: companyIndustry } : null,
    ].filter((fact): fact is CompanyContextFact => fact !== null);
    const recentSignals = Array.from(new Set(
        (data.recentActivities || [])
            .map((activity) => [
                activity.analysis?.headline,
                activity.analysis?.summary,
                activity.data?.activityDescription,
                activity.data?.text,
            ].find(isUsableText)?.trim() || '')
            .filter(Boolean)
    )).slice(0, 3);
    const companyContextNarrative = companyContextSection?.content.trim() || '';
    const showCompanyContextNarrative = companyContextNarrative.length > 0
        && companyContextNarrative.replace(/\s+/g, ' ').toLowerCase() !== executiveSummary.replace(/\s+/g, ' ').toLowerCase();
    const hasCompanyContext = companyContextFacts.length > 0 || recentSignals.length > 0 || showCompanyContextNarrative;
    const companyDetailCandidates: Array<CompanyDetailFact | null> = [
        isUsableText(data.companyName) ? { label: 'Company', value: data.companyName.trim() } : null,
        cnaeValue ? { label: 'CNAE / NACE', value: cnaeValue } : null,
        companyLocation ? { label: 'Headquarters', value: companyLocation } : null,
        employeeValue ? { label: 'Employees', value: employeeValue } : null,
        isUsableText(data.companyVat) ? { label: 'VAT / Tax ID', value: data.companyVat.trim() } : null,
        websiteURL ? { label: 'Website', value: cleanURL(websiteURL), href: websiteURL } : null,
        linkedInURL ? { label: 'LinkedIn', value: cleanURL(linkedInURL), href: linkedInURL } : null,
    ];
    const companyDetails = companyDetailCandidates.filter((detail): detail is CompanyDetailFact => detail !== null);
    const companyIntelligenceSummary = mergeCompanySummaries(data.rawCompanySummary, data.companySummary);
    const businessProfileGroups = buildCompanyTextGroups(companyIntelligenceSummary?.business_profile);
    const marketPositionGroups = buildCompanyTextGroups(companyIntelligenceSummary?.market_position);

    return (
        <SimpleTabContent>
            <div className="space-y-6">
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,3fr)_minmax(15rem,1fr)] xl:items-start">
                    <section
                        aria-labelledby={companySummaryHeadingId}
                        className="relative min-w-0 overflow-hidden rounded-[20px] border border-white/[0.05] bg-white/[0.03] p-4 sm:p-5"
                    >
                        <div className="pointer-events-none absolute right-0 top-0 h-52 w-52 translate-x-1/3 -translate-y-1/2 rounded-full bg-[#4FCDCF]/[0.055] blur-3xl" />
                        <div className="relative z-10 mb-4 flex items-start gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#4FCDCF]/10 text-[#66D9DB]">
                                <Icon icon="bx:buildings" className="h-5 w-5" aria-hidden="true" />
                            </div>
                            <div className="min-w-0 pt-0.5">
                                <h3 id={companySummaryHeadingId} className="text-xs font-bold uppercase tracking-[0.14em] text-[#A7E7E8]">Company Summary</h3>
                                <p className="mt-1 text-[11px] leading-4 text-[#91A5B8]">Executive summary and strategic context.</p>
                            </div>
                        </div>

                        <div className="relative z-10 min-w-0 text-sm leading-6 text-[#C9D1E7] sm:pl-12">
                            {executiveSummary ? (
                                <StreamdownRendererSimple
                                    content={executiveSummary}
                                    className="[&_.space-y-4]:space-y-2 [&_li]:my-0.5 [&_p]:my-0 [&_p]:max-w-[86ch] [&_p]:text-sm [&_p]:leading-6"
                                />
                            ) : (
                                <p className="italic text-gray-500">No executive summary available.</p>
                            )}
                        </div>

                        {hasCompanyContext && (
                            <div className="relative z-10 mt-4 border-t border-white/[0.065] pt-3 sm:ml-12">
                                <div className="mb-2.5 flex items-center gap-2">
                                    <Icon icon="bx:briefcase-alt-2" className="h-3.5 w-3.5 text-[#66D9DB]" aria-hidden="true" />
                                    <h4 className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#8DCFD1]">Company Context</h4>
                                </div>

                                {companyContextFacts.length > 0 && (
                                    <dl className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                        {companyContextFacts.map((fact) => (
                                            <div key={fact.label} className="min-w-0 rounded-lg border border-white/[0.055] bg-black/10 px-2.5 py-2">
                                                <dt className="text-[9px] font-bold uppercase tracking-[0.13em] text-[#8495A9]">{fact.label}</dt>
                                                <dd className="mt-0.5 break-words text-[12px] font-semibold leading-4.5 text-gray-200">{fact.value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                )}

                                {showCompanyContextNarrative && (
                                    <StreamdownRendererSimple
                                        content={companyContextNarrative}
                                        className="mt-2.5 [&_.space-y-4]:space-y-1.5 [&_li]:my-0 [&_li]:text-[13px] [&_li]:leading-5 [&_p]:my-0 [&_p]:text-[13px] [&_p]:leading-5"
                                    />
                                )}

                                {recentSignals.length > 0 && (
                                    <div className="mt-2.5">
                                        <div className="mb-1.5 flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.13em] text-[#8495A9]">
                                            <Icon icon="bx:bar-chart-alt-2" className="h-3 w-3 text-[#66D9DB]" aria-hidden="true" />
                                            Recent Signals
                                        </div>
                                        <ul className="grid grid-cols-1 gap-1.5 lg:grid-cols-2">
                                            {recentSignals.map((signal) => (
                                                <li key={signal} className="flex min-w-0 items-start gap-2 rounded-lg bg-white/[0.025] px-2.5 py-2 text-[12px] leading-4.5 text-gray-300">
                                                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#4FCDCF]" aria-hidden="true" />
                                                    <span className="min-w-0 break-words">{signal}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>

                    <section
                        aria-labelledby={companyDetailsHeadingId}
                        className="min-w-0 rounded-[20px] border border-[#4FCDCF]/15 bg-[linear-gradient(180deg,rgba(79,205,207,0.07),rgba(255,255,255,0.025))] p-4 sm:p-5"
                    >
                        <div className="mb-4 flex items-start justify-between gap-3">
                            <div className="flex min-w-0 items-start gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#4FCDCF]/10 text-[#66D9DB]">
                                    {data.companyLogo && !companyLogoError ? (
                                        <img
                                            src={data.companyLogo}
                                            alt=""
                                            className="h-full w-full object-contain p-1.5"
                                            onError={() => setCompanyLogoError(true)}
                                        />
                                    ) : (
                                        <Icon icon="bx:detail" className="h-5 w-5" aria-hidden="true" />
                                    )}
                                </div>
                                <div className="min-w-0 pt-0.5">
                                    <h3 id={companyDetailsHeadingId} className="text-xs font-bold uppercase tracking-[0.12em] text-[#A7E7E8]">Company Details</h3>
                                    <p className="mt-1 text-[11px] leading-4 text-[#91A5B8]">Verified account facts.</p>
                                </div>
                            </div>
                            {companyLevel1Id !== null && companyLevel1Id !== undefined && (
                                <span className="shrink-0 rounded-md border border-white/[0.07] bg-black/10 px-1.5 py-1 text-[10px] font-semibold text-gray-400">
                                    #{companyLevel1Id}
                                </span>
                            )}
                        </div>

                        {companyDetails.length > 0 ? (
                            <dl className="min-w-0 divide-y divide-white/[0.06]">
                                {companyDetails.map((detail) => (
                                    <div key={detail.label} className="min-w-0 py-2 first:pt-0 last:pb-0">
                                        <dt className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#8495A9]">{detail.label}</dt>
                                        <dd className="mt-0.5 break-words text-[13px] font-medium leading-5 text-gray-200">
                                            {detail.href ? (
                                                <a
                                                    href={detail.href}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex max-w-full items-center gap-1 break-all text-[#78DDE0] transition-colors hover:text-white hover:underline"
                                                >
                                                    <span>{detail.value}</span>
                                                    <Icon icon="bx:link-external" className="h-3 w-3 shrink-0" aria-hidden="true" />
                                                </a>
                                            ) : detail.value}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        ) : (
                            <p className="text-[13px] italic leading-5 text-gray-500">No verified company details available.</p>
                        )}
                    </section>
                </div>

                <ResearchSummarySections
                    title="Company Research"
                    sections={remainingCompanyResearchSections}
                    tab="company"
                />

                {businessProfileGroups.length > 0 && (
                    <Card>
                        <CardHeader
                            title="Business Profile"
                            icon={<Icon icon="bx:briefcase-alt-2" className="h-5 w-5" aria-hidden="true" />}
                            iconColor="text-cyan-300"
                        />
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                            {businessProfileGroups.map((group) => (
                                <div key={group.key} className="min-w-0 rounded-xl bg-white/[0.025] p-3">
                                    <SectionLabel label={group.label} />
                                    {group.items.length === 1 ? (
                                        <p className="break-words text-[13px] font-medium leading-5 text-gray-200">{group.items[0]}</p>
                                    ) : (
                                        <ul className="space-y-1">
                                            {group.items.map((item) => (
                                                <li key={item} className="flex items-start gap-2 text-[12px] leading-4.5 text-gray-300">
                                                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-cyan-300/60" aria-hidden="true" />
                                                    <span className="min-w-0 break-words">{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                {marketPositionGroups.length > 0 && (
                    <Card>
                        <CardHeader
                            title="Market Position"
                            icon={<Icon icon="bx:bar-chart-square" className="h-5 w-5" aria-hidden="true" />}
                            iconColor="text-blue-400"
                        />
                        <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${marketPositionGroups.length > 2 ? 'xl:grid-cols-3' : ''}`}>
                            {marketPositionGroups.map((group) => (
                                <div key={group.key} className="min-w-0 rounded-xl bg-white/[0.025] p-3">
                                    <SectionLabel label={group.label} className="text-blue-400" />
                                    {group.items.length === 1 ? (
                                        <p className="break-words text-[13px] leading-5 text-gray-300">{group.items[0]}</p>
                                    ) : (
                                        <ul className="space-y-1">
                                            {group.items.map((item) => (
                                                <li key={item} className="flex items-start gap-2 text-[12px] leading-4.5 text-gray-300">
                                                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-blue-300/70" aria-hidden="true" />
                                                    <span className="min-w-0 break-words">{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Card>
                )}
            </div>
        </SimpleTabContent>
    );
};
